import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  Send,
} from "lucide-react";
import { Link } from "react-router";
import { Toaster, toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useBusinessAccounts } from "@/hooks/useBusinesses";
import {
  useCreateNewsletterIssue,
  useNewsletterIssue,
  useNewsletterIssues,
  useNewsletterSubmissions,
  usePatchNewsletterIssue,
  usePatchNewsletterSubmission,
  useRenderNewsletterIssue,
  useSendNewsletterIssue,
  useTestSendNewsletterIssue,
} from "@/hooks/useNewsletter";
import { useSessionState } from "@/hooks/useSessionState";
import { useUrlState } from "@/hooks/useUrlState";
import type { UrlStatePatch } from "@/hooks/useUrlState";
import { ApiError } from "@/services/api";
import { fileUrl } from "@/utils/fileUrl";
import type { BusinessAccount } from "@/services/businessApi";
import type {
  CreateNewsletterIssueRequest,
  NewsletterIssue,
  NewsletterSubmission,
  ListNewsletterSubmissionsParams,
} from "@/services/newsletterApi";
import type {
  NewsletterAdminSessionState,
  NewsletterAdminUrlState,
} from "@/types/pageState";

const ALL_VALUE = "__all";

const SUBMISSION_STATUSES = [
  "draft",
  "submitted",
  "approved",
  "rejected",
  "included",
] as const;

type NewsletterAttachmentItem = NonNullable<
  NewsletterSubmission["attachments"]
>[number];

interface IssueFormState {
  title: string;
  subject: string;
  introHtml: string;
}

const defaultIssueForm: IssueFormState = {
  title: "",
  subject: "",
  introHtml: "",
};

function formatDate(iso: string | null): string {
  if (!iso) return "None";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "active":
    case "approved":
    case "sent":
      return "default";
    case "submitted":
    case "review":
    case "scheduled":
      return "secondary";
    case "past_due":
    case "rejected":
      return "destructive";
    default:
      return "outline";
  }
}

function handleApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    toast.error(error.message);
    return;
  }
  toast.error(error instanceof Error ? error.message : fallback);
}

function issuePayloadFromForm(
  form: IssueFormState,
): CreateNewsletterIssueRequest {
  return {
    title: form.title.trim(),
    subject: form.subject.trim() || null,
    intro_html: form.introHtml.trim() || null,
  };
}

function heroUrl(submission: NewsletterSubmission): string | null {
  if (submission.image_url) return fileUrl(submission.image_url);
  // Legacy fallback: image_path was a full URL before the S3 migration
  if (
    submission.image_path &&
    (submission.image_path.startsWith("http://") ||
      submission.image_path.startsWith("https://") ||
      submission.image_path.startsWith("/"))
  ) {
    return fileUrl(submission.image_path);
  }
  return null;
}

function attachmentHref(attachment: NewsletterAttachmentItem): string | null {
  if (attachment.url) return fileUrl(attachment.url);
  // Legacy fallback: path was a fetchable URL before the S3 migration
  if (
    attachment.path &&
    (attachment.path.startsWith("http://") ||
      attachment.path.startsWith("https://") ||
      attachment.path.startsWith("/"))
  ) {
    return fileUrl(attachment.path);
  }
  return null;
}

function attachmentLabel(attachment: NewsletterAttachmentItem): string {
  return String(
    attachment.original_name ??
      attachment.filename ??
      attachment.name ??
      attachment.key ??
      "Attachment",
  );
}

export default function AdminNewsletter() {
  const [urlState, setUrlState] = useUrlState<NewsletterAdminUrlState>({
    schema: {
      tab: "string",
      submission_status: "string",
      submission_business_id: "number",
      selected_issue_id: "number",
    },
    defaults: { tab: "submissions" },
    replace: true,
    debounceMs: 200,
  });

  const [sessionState, setSessionState] =
    useSessionState<NewsletterAdminSessionState>({
      key: "newsletter-admin-ui",
      defaultValue: {
        reviewSubmissionId: null,
        createIssueDialogOpen: false,
        testSendDialogOpen: false,
        sendConfirmIssueId: null,
      },
    });

  const [submissionNotes, setSubmissionNotes] = useState("");
  const [issueForm, setIssueForm] = useState<IssueFormState>(defaultIssueForm);
  const [blockOrder, setBlockOrder] = useState<number[]>([]);
  const [testRecipient, setTestRecipient] = useState("");

  const activeTab = urlState.tab ?? "submissions";
  const selectedIssueId = urlState.selected_issue_id ?? null;
  const submissionParams = useMemo<ListNewsletterSubmissionsParams>(() => {
    const params: ListNewsletterSubmissionsParams = {};
    if (urlState.submission_status) {
      params.status_filter = urlState.submission_status;
    }
    if (urlState.submission_business_id != null) {
      params.business_id = urlState.submission_business_id;
    }
    return params;
  }, [urlState.submission_business_id, urlState.submission_status]);

  const businessesQuery = useBusinessAccounts();
  const submissionsQuery = useNewsletterSubmissions(submissionParams);
  const approvedSubmissionsQuery = useNewsletterSubmissions({
    status_filter: "approved",
  });
  const issuesQuery = useNewsletterIssues();
  const issueDetailsQuery = useNewsletterIssue(selectedIssueId);

  const patchSubmission = usePatchNewsletterSubmission();
  const createIssue = useCreateNewsletterIssue();
  const patchIssue = usePatchNewsletterIssue();
  const renderIssue = useRenderNewsletterIssue();
  const testSendIssue = useTestSendNewsletterIssue();
  const sendIssue = useSendNewsletterIssue();

  const businesses = businessesQuery.data ?? [];
  const submissions = submissionsQuery.data ?? [];
  const approvedSubmissions = approvedSubmissionsQuery.data ?? [];
  const issues = issuesQuery.data ?? [];
  const selectedIssue =
    issueDetailsQuery.data ??
    issues.find((issue) => issue.id === selectedIssueId) ??
    null;

  const businessById = useMemo(() => {
    const map = new Map<number, BusinessAccount>();
    for (const business of businesses) map.set(business.id, business);
    return map;
  }, [businesses]);

  const eligibleSubmissions = useMemo(
    () =>
      approvedSubmissions.filter((submission) => {
        const business = businessById.get(submission.business_id);
        return business?.is_eligible;
      }),
    [approvedSubmissions, businessById],
  );

  const submissionToReview =
    sessionState.reviewSubmissionId != null
      ? (submissions.find(
          (sub) => sub.id === sessionState.reviewSubmissionId,
        ) ??
        approvedSubmissions.find(
          (sub) => sub.id === sessionState.reviewSubmissionId,
        ) ??
        null)
      : null;

  useEffect(() => {
    if (!selectedIssueId && issues.length > 0) {
      setUrlState((prev) => ({ ...prev, selected_issue_id: issues[0]!.id }));
    }
  }, [issues, selectedIssueId, setUrlState]);

  useEffect(() => {
    if (!selectedIssue) {
      setIssueForm(defaultIssueForm);
      setBlockOrder([]);
      return;
    }
    setIssueForm({
      title: selectedIssue.title,
      subject: selectedIssue.subject ?? "",
      introHtml: selectedIssue.intro_html ?? "",
    });
    setBlockOrder(selectedIssue.block_order ?? []);
  }, [selectedIssue]);

  function openSubmissionDialog(submission: NewsletterSubmission) {
    setSubmissionNotes(submission.admin_notes ?? "");
    setSessionState((prev) => ({ ...prev, reviewSubmissionId: submission.id }));
  }

  async function submitSubmissionPatch(status?: string) {
    if (!submissionToReview) return;
    try {
      const updated = await patchSubmission.mutateAsync({
        submissionId: submissionToReview.id,
        admin_notes: submissionNotes.trim() || null,
        ...(status ? { status } : {}),
      });
      toast.success(`Submission ${updated.id} updated.`);
      setSessionState((prev) => ({ ...prev, reviewSubmissionId: null }));
    } catch (error) {
      handleApiError(error, "Failed to update submission.");
    }
  }

  async function submitCreateIssue() {
    try {
      const created = await createIssue.mutateAsync(
        issuePayloadFromForm(issueForm),
      );
      toast.success(`Created ${created.title}.`);
      setUrlState((prev) => ({
        ...prev,
        tab: "issues",
        selected_issue_id: created.id,
      }));
      setSessionState((prev) => ({ ...prev, createIssueDialogOpen: false }));
      setIssueForm(defaultIssueForm);
    } catch (error) {
      handleApiError(error, "Failed to create issue.");
    }
  }

  async function submitIssueDetails() {
    if (!selectedIssue) return;
    try {
      const updated = await patchIssue.mutateAsync({
        issueId: selectedIssue.id,
        ...issuePayloadFromForm(issueForm),
      });
      toast.success(`Saved ${updated.title}.`);
    } catch (error) {
      handleApiError(error, "Failed to save issue.");
    }
  }

  async function submitBlockOrder() {
    if (!selectedIssue) return;
    try {
      await patchIssue.mutateAsync({
        issueId: selectedIssue.id,
        block_order: blockOrder,
      });
      toast.success("Saved issue block order.");
    } catch (error) {
      handleApiError(error, "Failed to save block order.");
    }
  }

  async function submitRenderIssue() {
    if (!selectedIssue) return;
    try {
      await renderIssue.mutateAsync(selectedIssue.id);
      toast.success("Rendered newsletter preview.");
    } catch (error) {
      handleApiError(error, "Failed to render issue.");
    }
  }

  async function submitTestSend() {
    if (!selectedIssue) return;
    try {
      const result = await testSendIssue.mutateAsync({
        issueId: selectedIssue.id,
        recipientEmail: testRecipient.trim(),
      });
      toast.success(
        result.sent ? "Test email sent." : "Test send returned false.",
      );
      setSessionState((prev) => ({ ...prev, testSendDialogOpen: false }));
      setTestRecipient("");
    } catch (error) {
      handleApiError(error, "Failed to send test email.");
    }
  }

  async function submitSendIssue() {
    if (sessionState.sendConfirmIssueId == null) return;
    try {
      const result = await sendIssue.mutateAsync(
        sessionState.sendConfirmIssueId,
      );
      toast.success(
        result.job_id
          ? `Newsletter send enqueued as job ${result.job_id}.`
          : "Newsletter send enqueued.",
      );
      setSessionState((prev) => ({ ...prev, sendConfirmIssueId: null }));
    } catch (error) {
      handleApiError(error, "Failed to enqueue newsletter send.");
    }
  }

  function toggleBlock(submissionId: number, checked: boolean) {
    setBlockOrder((prev) =>
      checked
        ? [...prev, submissionId]
        : prev.filter((id) => id !== submissionId),
    );
  }

  function moveBlock(submissionId: number, direction: -1 | 1) {
    setBlockOrder((prev) => {
      const index = prev.indexOf(submissionId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex]!, next[index]!];
      return next;
    });
  }

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Newsletter Admin</h2>
            <p className="text-sm text-muted-foreground">
              Manage submitted blocks and assemble newsletter issues.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/businesses"
              className={buttonVariants({ variant: "outline" })}
            >
              Manage Businesses
            </Link>
            <Button
              onClick={() => {
                setIssueForm(defaultIssueForm);
                setSessionState((prev) => ({
                  ...prev,
                  createIssueDialogOpen: true,
                }));
              }}
            >
              <Mail className="size-4" />
              Create Issue
            </Button>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setUrlState((prev) => ({
              ...prev,
              tab: value as NewsletterAdminUrlState["tab"],
            }))
          }
        >
          <TabsList>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="issues">Issues</TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="space-y-4">
            <SubmissionFilters
              businesses={businesses}
              urlState={urlState}
              setUrlState={setUrlState}
            />
            <SubmissionTable
              submissions={submissions}
              businesses={businessById}
              isLoading={submissionsQuery.isLoading}
              onReview={openSubmissionDialog}
            />
          </TabsContent>

          <TabsContent value="issues" className="space-y-4">
            <IssuesWorkspace
              issues={issues}
              issuesLoading={issuesQuery.isLoading}
              selectedIssue={selectedIssue}
              selectedIssueLoading={issueDetailsQuery.isLoading}
              selectedIssueId={selectedIssueId}
              onSelectIssue={(issueId) =>
                setUrlState((prev) => ({ ...prev, selected_issue_id: issueId }))
              }
              issueForm={issueForm}
              setIssueForm={setIssueForm}
              onSaveIssue={submitIssueDetails}
              issueSaving={patchIssue.isPending}
              eligibleSubmissions={eligibleSubmissions}
              businesses={businessById}
              blockOrder={blockOrder}
              onToggleBlock={toggleBlock}
              onMoveBlock={moveBlock}
              onSaveBlockOrder={submitBlockOrder}
              onRender={submitRenderIssue}
              renderPending={renderIssue.isPending}
              onOpenTestSend={() =>
                setSessionState((prev) => ({
                  ...prev,
                  testSendDialogOpen: true,
                }))
              }
              onOpenSendConfirm={(issueId) =>
                setSessionState((prev) => ({
                  ...prev,
                  sendConfirmIssueId: issueId,
                }))
              }
            />
          </TabsContent>
        </Tabs>
      </div>

      <SubmissionDialog
        submission={submissionToReview}
        business={
          submissionToReview
            ? (businessById.get(submissionToReview.business_id) ?? null)
            : null
        }
        notes={submissionNotes}
        setNotes={setSubmissionNotes}
        open={sessionState.reviewSubmissionId != null}
        onOpenChange={(open) =>
          setSessionState((prev) => ({
            ...prev,
            reviewSubmissionId: open ? prev.reviewSubmissionId : null,
          }))
        }
        onSave={() => submitSubmissionPatch()}
        onApprove={() => submitSubmissionPatch("approved")}
        onReject={() => submitSubmissionPatch("rejected")}
        submitting={patchSubmission.isPending}
      />

      <CreateIssueDialog
        open={sessionState.createIssueDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, createIssueDialogOpen: open }))
        }
        form={issueForm}
        setForm={setIssueForm}
        onSubmit={submitCreateIssue}
        submitting={createIssue.isPending}
      />

      <Dialog
        open={sessionState.testSendDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, testSendDialogOpen: open }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send the rendered issue to one recipient before enqueuing the real
              send.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="test-recipient">Recipient email</Label>
            <Input
              id="test-recipient"
              type="email"
              value={testRecipient}
              onChange={(event) => setTestRecipient(event.target.value)}
              placeholder="admin@example.com"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({
                  ...prev,
                  testSendDialogOpen: false,
                }))
              }
            >
              Cancel
            </Button>
            <Button
              onClick={submitTestSend}
              disabled={!testRecipient.trim() || testSendIssue.isPending}
            >
              {testSendIssue.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={sessionState.sendConfirmIssueId != null}
        onOpenChange={(open) =>
          setSessionState((prev) => ({
            ...prev,
            sendConfirmIssueId: open ? prev.sendConfirmIssueId : null,
          }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enqueue Newsletter Send?</DialogTitle>
            <DialogDescription>
              This will enqueue a worker job for all opted-in newsletter
              recipients. Render and test the issue before continuing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({
                  ...prev,
                  sendConfirmIssueId: null,
                }))
              }
            >
              Cancel
            </Button>
            <Button onClick={submitSendIssue} disabled={sendIssue.isPending}>
              {sendIssue.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Enqueue Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SubmissionFilters({
  businesses,
  urlState,
  setUrlState,
}: {
  businesses: BusinessAccount[];
  urlState: NewsletterAdminUrlState;
  setUrlState: (
    updates:
      | UrlStatePatch<NewsletterAdminUrlState>
      | ((
          prev: NewsletterAdminUrlState,
        ) => UrlStatePatch<NewsletterAdminUrlState>),
  ) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Submission Status</Label>
        <Select
          value={urlState.submission_status ?? ALL_VALUE}
          onValueChange={(value) =>
            setUrlState((prev) => ({
              ...prev,
              submission_status: value === ALL_VALUE ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
            {SUBMISSION_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Business</Label>
        <Select
          value={
            urlState.submission_business_id == null
              ? ALL_VALUE
              : String(urlState.submission_business_id)
          }
          onValueChange={(value) =>
            setUrlState((prev) => ({
              ...prev,
              submission_business_id:
                value === ALL_VALUE ? undefined : Number(value),
            }))
          }
        >
          <SelectTrigger className="h-8 w-64 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All businesses</SelectItem>
            {businesses.map((business) => (
              <SelectItem key={business.id} value={String(business.id)}>
                {business.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function SubmissionTable({
  submissions,
  businesses,
  isLoading,
  onReview,
}: {
  submissions: NewsletterSubmission[];
  businesses: Map<number, BusinessAccount>;
  isLoading: boolean;
  onReview: (submission: NewsletterSubmission) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Submissions ({submissions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Submission</TableHead>
              <TableHead>Business</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Assets</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Loader2 className="size-4 animate-spin" />
                </TableCell>
              </TableRow>
            ) : submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">
                  No submissions match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((submission) => {
                const business = businesses.get(submission.business_id);
                return (
                  <TableRow key={submission.id}>
                    <TableCell className="max-w-md">
                      <div className="font-medium">
                        {submission.headline ?? `Submission #${submission.id}`}
                      </div>
                      <div className="line-clamp-2 text-xs text-muted-foreground">
                        {submission.blurb ?? "No blurb provided."}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {business?.name ??
                          `Business #${submission.business_id}`}
                      </div>
                      <Badge
                        variant={
                          business?.is_eligible ? "default" : "secondary"
                        }
                        className="mt-1"
                      >
                        {business?.is_eligible ? "Eligible" : "Not eligible"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(submission.status)}>
                        {submission.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <SubmissionAssets submission={submission} compact />
                    </TableCell>
                    <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onReview(submission)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function IssuesWorkspace({
  issues,
  issuesLoading,
  selectedIssue,
  selectedIssueLoading,
  selectedIssueId,
  onSelectIssue,
  issueForm,
  setIssueForm,
  onSaveIssue,
  issueSaving,
  eligibleSubmissions,
  businesses,
  blockOrder,
  onToggleBlock,
  onMoveBlock,
  onSaveBlockOrder,
  onRender,
  renderPending,
  onOpenTestSend,
  onOpenSendConfirm,
}: {
  issues: NewsletterIssue[];
  issuesLoading: boolean;
  selectedIssue: NewsletterIssue | null;
  selectedIssueLoading: boolean;
  selectedIssueId: number | null;
  onSelectIssue: (issueId: number) => void;
  issueForm: IssueFormState;
  setIssueForm: (
    value: IssueFormState | ((prev: IssueFormState) => IssueFormState),
  ) => void;
  onSaveIssue: () => void;
  issueSaving: boolean;
  eligibleSubmissions: NewsletterSubmission[];
  businesses: Map<number, BusinessAccount>;
  blockOrder: number[];
  onToggleBlock: (submissionId: number, checked: boolean) => void;
  onMoveBlock: (submissionId: number, direction: -1 | 1) => void;
  onSaveBlockOrder: () => void;
  onRender: () => void;
  renderPending: boolean;
  onOpenTestSend: () => void;
  onOpenSendConfirm: (issueId: number) => void;
}) {
  const selectedBlockSet = useMemo(() => new Set(blockOrder), [blockOrder]);
  const orderedBlocks = blockOrder
    .map((id) => eligibleSubmissions.find((submission) => submission.id === id))
    .filter((submission): submission is NewsletterSubmission =>
      Boolean(submission),
    );
  const detailIssue = selectedIssue as
    | (NewsletterIssue & { rendered_html?: string | null })
    | null;

  return (
    <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Issues ({issues.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {issuesLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : issues.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No issues yet. Create one to assemble a newsletter.
            </p>
          ) : (
            issues.map((issue) => (
              <button
                key={issue.id}
                type="button"
                onClick={() => onSelectIssue(issue.id)}
                className={`w-full rounded-md border p-3 text-left text-sm transition hover:bg-muted ${
                  selectedIssueId === issue.id ? "border-primary bg-muted" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{issue.title}</span>
                  <Badge variant={statusVariant(issue.status)}>
                    {issue.status}
                  </Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {issue.subject ?? "No subject"} -{" "}
                  {formatDate(issue.created_at)}
                </div>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {!selectedIssue ? (
          <Card>
            <CardContent className="py-8 text-sm text-muted-foreground">
              Select or create an issue to edit newsletter content.
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base">Issue Details</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRender}
                      disabled={
                        renderPending || selectedIssue.status === "sent"
                      }
                    >
                      {renderPending && (
                        <Loader2 className="size-4 animate-spin" />
                      )}
                      Render
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onOpenTestSend}
                      disabled={!detailIssue?.rendered_html}
                    >
                      <Mail className="size-4" />
                      Test Send
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onOpenSendConfirm(selectedIssue.id)}
                      disabled={
                        !detailIssue?.rendered_html ||
                        selectedIssue.status === "sent"
                      }
                    >
                      <Send className="size-4" />
                      Send
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="issue-title">Title</Label>
                    <Input
                      id="issue-title"
                      value={issueForm.title}
                      onChange={(event) =>
                        setIssueForm((prev) => ({
                          ...prev,
                          title: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="issue-subject">Subject</Label>
                    <Input
                      id="issue-subject"
                      value={issueForm.subject}
                      onChange={(event) =>
                        setIssueForm((prev) => ({
                          ...prev,
                          subject: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="issue-intro">Intro HTML</Label>
                    <Textarea
                      id="issue-intro"
                      className="min-h-40 font-mono text-xs"
                      value={issueForm.introHtml}
                      onChange={(event) =>
                        setIssueForm((prev) => ({
                          ...prev,
                          introHtml: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <Button
                    onClick={onSaveIssue}
                    disabled={!issueForm.title.trim() || issueSaving}
                  >
                    {issueSaving && <Loader2 className="size-4 animate-spin" />}
                    Save Details
                  </Button>
                </div>
                <div className="space-y-2 rounded-md border p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant(selectedIssue.status)}>
                      {selectedIssue.status}
                    </Badge>
                    {selectedIssueLoading && (
                      <Loader2 className="size-4 animate-spin" />
                    )}
                  </div>
                  <div>Created: {formatDate(selectedIssue.created_at)}</div>
                  <div>Sent: {formatDate(selectedIssue.sent_at)}</div>
                  <div>
                    Recipients: {selectedIssue.recipient_count ?? "Not sent"}
                  </div>
                  <div>Blocks: {blockOrder.length}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">Eligible Blocks</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onSaveBlockOrder}
                    disabled={selectedIssue.status === "sent"}
                  >
                    <CheckCircle2 className="size-4" />
                    Save Block Order
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">
                    Approved eligible submissions
                  </h3>
                  {eligibleSubmissions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No approved tier 1/2 submissions are available.
                    </p>
                  ) : (
                    eligibleSubmissions.map((submission) => {
                      const business = businesses.get(submission.business_id);
                      return (
                        <label
                          key={submission.id}
                          className="flex cursor-pointer gap-3 rounded-md border p-3 text-sm"
                        >
                          <Checkbox
                            checked={selectedBlockSet.has(submission.id)}
                            onCheckedChange={(checked) =>
                              onToggleBlock(submission.id, checked === true)
                            }
                          />
                          <div>
                            <div className="font-medium">
                              {submission.headline ??
                                `Submission #${submission.id}`}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {business?.name ??
                                `Business #${submission.business_id}`}
                            </div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Issue order</h3>
                  {orderedBlocks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Select submissions to add blocks to this issue.
                    </p>
                  ) : (
                    orderedBlocks.map((submission, index) => (
                      <div
                        key={submission.id}
                        className="flex items-center gap-2 rounded-md border p-3 text-sm"
                      >
                        <div className="flex-1">
                          <div className="font-medium">
                            {index + 1}. {submission.headline}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {businesses.get(submission.business_id)?.name ??
                              `Business #${submission.business_id}`}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onMoveBlock(submission.id, -1)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onMoveBlock(submission.id, 1)}
                          disabled={index === orderedBlocks.length - 1}
                        >
                          <ArrowDown className="size-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Rendered Preview</CardTitle>
              </CardHeader>
              <CardContent>
                {detailIssue?.rendered_html ? (
                  <iframe
                    title="Rendered newsletter preview"
                    className="h-[640px] w-full rounded-md border bg-white"
                    srcDoc={detailIssue.rendered_html}
                    sandbox=""
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Render the issue to preview generated HTML.
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function SubmissionDialog({
  submission,
  business,
  notes,
  setNotes,
  open,
  onOpenChange,
  onSave,
  onApprove,
  onReject,
  submitting,
}: {
  submission: NewsletterSubmission | null;
  business: BusinessAccount | null;
  notes: string;
  setNotes: (notes: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  onApprove: () => void;
  onReject: () => void;
  submitting: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Review Submission</DialogTitle>
          <DialogDescription>
            Approve or reject submitted material and keep admin notes.
          </DialogDescription>
        </DialogHeader>
        {submission ? (
          <div className="space-y-4">
            <div className="rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium">
                  {submission.headline ?? `Submission #${submission.id}`}
                </h3>
                <Badge variant={statusVariant(submission.status)}>
                  {submission.status}
                </Badge>
                <Badge
                  variant={business?.is_eligible ? "default" : "secondary"}
                >
                  {business?.is_eligible ? "Eligible" : "Not eligible"}
                </Badge>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {submission.blurb ?? "No blurb provided."}
              </p>
              <div className="mt-3 text-sm">
                Business:{" "}
                {business?.name ?? `Business #${submission.business_id}`}
              </div>
              {submission.cta_url && (
                <a
                  className="mt-2 inline-flex items-center gap-1 text-sm text-primary"
                  href={submission.cta_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {submission.cta_label ?? submission.cta_url}
                  <ExternalLink className="size-3" />
                </a>
              )}
            </div>
            <SubmissionAssets submission={submission} />
            <div className="space-y-1.5">
              <Label htmlFor="submission-notes">Admin Notes</Label>
              <Textarea
                id="submission-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className="min-h-32"
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Submission not found.</p>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onSave} disabled={submitting}>
              Save Notes
            </Button>
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={submitting}
            >
              Reject
            </Button>
            <Button onClick={onApprove} disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              Approve
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionAssets({
  submission,
  compact = false,
}: {
  submission: NewsletterSubmission;
  compact?: boolean;
}) {
  const attachments = submission.attachments ?? [];
  const hero = submission.image_path ? heroUrl(submission) : null;
  if (!hero && attachments.length === 0) {
    return <span className="text-sm text-muted-foreground">No assets</span>;
  }

  return (
    <div className={compact ? "space-y-1 text-xs" : "space-y-2 text-sm"}>
      {hero && (
        <div className="space-y-1">
          {!compact && (
            <img
              src={hero}
              alt="Hero image"
              className="max-h-32 max-w-xs rounded border object-cover"
            />
          )}
          <a
            href={hero}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary"
          >
            Hero image
            <ExternalLink className="size-3" />
          </a>
        </div>
      )}
      {attachments.map((attachment, index) => {
        const href = attachmentHref(attachment);
        const label = attachmentLabel(attachment);
        return href ? (
          <a
            key={`${href}-${index}`}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="block text-primary"
          >
            {label}
          </a>
        ) : (
          <span
            key={`attachment-${index}`}
            className="block text-muted-foreground"
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

function CreateIssueDialog({
  open,
  onOpenChange,
  form,
  setForm,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: IssueFormState;
  setForm: (
    value: IssueFormState | ((prev: IssueFormState) => IssueFormState),
  ) => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Newsletter Issue</DialogTitle>
          <DialogDescription>
            Start a draft issue. Blocks can be added after creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="create-issue-title">Title</Label>
            <Input
              id="create-issue-title"
              value={form.title}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-issue-subject">Subject</Label>
            <Input
              id="create-issue-subject"
              value={form.subject}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, subject: event.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-issue-intro">Intro HTML</Label>
            <Textarea
              id="create-issue-intro"
              value={form.introHtml}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, introHtml: event.target.value }))
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!form.title.trim() || submitting}
          >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            Create Issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
